import type { AutoLabelLibraryJobView } from '@/api/api';
import { useAutoLabelLibrary } from '@/hooks/api/taxonomy/useAutoLabelLibrary';
import { useUpdateTaxonomy } from '@/hooks/api/taxonomy/useUpdateTaxonomy';
import { CheckCircle, Cancel, ErrorOutline } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

interface AutoLabelLibraryModalProperties {
  isOpen: boolean;
  onClose: () => void;
  onJobStarted: (jobId: string) => void;
  onDismissTerminal: () => void;
  taxonomyId: string;
  autoLabelOptedIn: boolean;
  job?: AutoLabelLibraryJobView;
}

const TERMINAL_LABEL: Record<'COMPLETED' | 'FAILED' | 'CANCELLED', string> = {
  COMPLETED: 'Auto-labeling complete',
  FAILED: 'Auto-labeling failed',
  CANCELLED: 'Auto-labeling cancelled',
};

const getJobStatusLabel = (job: AutoLabelLibraryJobView): string => {
  if (job.status === 'COMPLETED' || job.status === 'FAILED' || job.status === 'CANCELLED') {
    return TERMINAL_LABEL[job.status];
  }
  return job.progress > 0 ? `Auto-labeling… ${job.progress}%` : 'Auto-labeling…';
};

export const AutoLabelLibraryModal = ({
  isOpen,
  onClose,
  onJobStarted,
  onDismissTerminal,
  taxonomyId,
  autoLabelOptedIn,
  job,
}: AutoLabelLibraryModalProperties) => {
  const [reclassifyAll, setReclassifyAll] = useState(false);
  const { startAutoLabelLibrary, isStartingAutoLabel, autoLabelStartError, resetAutoLabelStartError } =
    useAutoLabelLibrary(taxonomyId);
  const { updateTaxonomy, isUpdating: isUpdatingOptIn, updateError } = useUpdateTaxonomy(taxonomyId);

  const handleClose = () => {
    setReclassifyAll(false);
    resetAutoLabelStartError();
    onClose();
  };

  const handleAutoLabel = () => {
    startAutoLabelLibrary(
      { skipAutoLabeled: !reclassifyAll },
      {
        onSuccess: ({ jobId }) => {
          onJobStarted(jobId);
          setReclassifyAll(false);
        },
      }
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={isStartingAutoLabel ? undefined : handleClose}
      slotProps={{ paper: { sx: { borderRadius: '12px', width: '560px', maxWidth: '560px' } } }}
    >
      <DialogTitle>Auto-label options</DialogTitle>
      <DialogContent>
        {autoLabelStartError && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {autoLabelStartError.message}
          </Alert>
        )}
        {updateError && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {updateError.message}
          </Alert>
        )}
        <Paper variant='outlined' sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant='body2' sx={{ fontWeight: 500 }}>
                Auto-label new assets as they&apos;re uploaded
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Newly indexed assets get auto-classified into your taxonomy.
              </Typography>
            </Box>
            <ToggleButtonGroup
              size='small'
              exclusive
              value={autoLabelOptedIn ? 'on' : 'off'}
              disabled={isUpdatingOptIn}
              onChange={(_event, value) => {
                if (value !== null) updateTaxonomy({ autoLabelOptedIn: value === 'on' });
              }}
              data-testid='auto-label-library-opt-in-toggle'
            >
              <ToggleButton value='off' sx={{ textTransform: 'none', px: 2 }}>
                Off
              </ToggleButton>
              <ToggleButton value='on' sx={{ textTransform: 'none', px: 2 }}>
                On
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Paper>
        <Paper variant='outlined' sx={{ p: 2, borderRadius: 2, mt: 2 }}>
          <Typography variant='body2' sx={{ fontWeight: 500 }}>
            One-time auto-label
          </Typography>
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
            Classify every finished asset in this tenant against your taxonomy. Manually labeled assets are always
            preserved.
          </Typography>
          {job ? (
            <Box sx={{ mt: 2 }} data-testid='auto-label-library-progress'>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                {job.status === 'COMPLETED' && <CheckCircle color='success' sx={{ fontSize: 18 }} />}
                {job.status === 'FAILED' && <ErrorOutline color='error' sx={{ fontSize: 18 }} />}
                {job.status === 'CANCELLED' && <Cancel color='disabled' sx={{ fontSize: 18 }} />}
                {(job.status === 'PENDING' || job.status === 'RUNNING') && <CircularProgress size={16} />}
                <Typography variant='body2' sx={{ fontWeight: 500 }}>
                  {getJobStatusLabel(job)}
                </Typography>
              </Box>
              <LinearProgress
                variant={job.status === 'PENDING' && job.progress === 0 ? 'indeterminate' : 'determinate'}
                value={job.status === 'COMPLETED' ? 100 : job.progress}
                color={job.status === 'FAILED' ? 'error' : 'primary'}
                sx={{ height: 6, borderRadius: 3 }}
              />
              <Box sx={{ display: 'flex', gap: 4, mt: 1.5 }}>
                <Box>
                  <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                    Queued
                  </Typography>
                  <Typography variant='body2' data-testid='auto-label-library-queued-count'>
                    {job.queuedCount}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                    Completed
                  </Typography>
                  <Typography variant='body2' data-testid='auto-label-library-completed-count'>
                    {job.completedCount}
                  </Typography>
                </Box>
                <Box>
                  <Tooltip title="Manually labeled assets are always skipped. Assets with existing AI labels are also skipped unless 'Re-classify assets that already have an AI-generated label' was checked when starting the job.">
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{
                        display: 'block',
                        width: 'fit-content',
                        cursor: 'help',
                        textDecoration: 'underline dotted',
                        textUnderlineOffset: '2px',
                      }}
                    >
                      Skipped
                    </Typography>
                  </Tooltip>
                  <Typography variant='body2' data-testid='auto-label-library-skipped-count'>
                    {job.skippedCount}
                  </Typography>
                </Box>
              </Box>
              {(job.status === 'PENDING' || job.status === 'RUNNING') && (
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1.5 }}>
                  You can close this dialog or navigate away — labeling continues in the background.
                </Typography>
              )}
              {job.status === 'FAILED' && (
                <Alert
                  severity='error'
                  sx={{ mt: 1.5 }}
                  action={
                    <Button
                      color='inherit'
                      size='small'
                      onClick={onDismissTerminal}
                      data-testid='auto-label-library-try-again'
                    >
                      Try again
                    </Button>
                  }
                >
                  The auto-label job didn&apos;t finish. Try again, or contact support if this keeps happening.
                </Alert>
              )}
            </Box>
          ) : (
            <>
              <Box sx={{ mt: 1.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={reclassifyAll}
                      onChange={event => setReclassifyAll(event.target.checked)}
                      disabled={isStartingAutoLabel}
                      size='small'
                    />
                  }
                  label={
                    <Typography variant='body2'>Re-classify assets that already have an AI-generated label</Typography>
                  }
                />
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block', ml: 4, mt: -0.5 }}>
                  By default already-labeled assets are skipped.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant='contained'
                  disabled={isStartingAutoLabel}
                  sx={{ minWidth: '160px' }}
                  onClick={handleAutoLabel}
                  data-testid='taxonomy-auto-label-library-confirm'
                >
                  {isStartingAutoLabel ? <CircularProgress size={20} /> : 'Auto-label now'}
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isStartingAutoLabel}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
